/**
 * Fixed asset — `/finance/fixed-assets/$fixedAssetId`.
 *
 * Two accounting rules are visible in the layout rather than buried:
 *
 * - **Net book value counts only depreciation that actually POSTED.** Deducting
 *   scheduled-but-unposted future charges would manufacture a loss out of a
 *   plan, so the schedule marks posted rows apart from scheduled ones and the
 *   disposal preview is computed from the posted figure the server reports.
 * - **An estimate revision is prospective.** Periods that already posted are
 *   left exactly as they are and the undepreciated balance is spread over the
 *   periods that remain — no catch-up entry, no restatement. The result reports
 *   how many periods were left untouched and how many were regenerated.
 */
import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button, Card, CardContent, CardHeader, CardTitle } from "@erp/ui";
import { ArrowLeft } from "lucide-react";

import { surfaceProblem } from "@/components/form";
import { MoneyDisplay } from "@/components/money/money-display";
import { useIdempotentMutation } from "@/features/shared/ops-api";
import { formatQuantity } from "@/lib/format";

import {
  disposeFixedAsset,
  reviseFixedAssetEstimates,
  type FixedAssetDisposal,
  type FixedAssetDisposalCreate,
  type FixedAssetEstimateRevision,
  type FixedAssetEstimateRevisionResult,
} from "../api";
import { AMOUNT_SCALE, normalizeDecimal, todayIsoDate } from "../decimal";
import {
  DateField,
  DecimalField,
  DefinitionList,
  NoticeBanner,
  PageHeader,
  ProblemBanner,
  StatusBadge,
  TextField,
} from "../components/fields";
import {
  financeKeys,
  useAssetClasses,
  useDepreciationSchedule,
  useFiscalPeriods,
  useFixedAsset,
} from "../queries";

const ASSET_STATUS_VARIANTS = {
  draft: "outline",
  active: "success",
  disposed: "secondary",
} as const;

export interface FixedAssetPageProps {
  fixedAssetId?: string;
  onBack: () => void;
  canRevise?: boolean;
  canDispose?: boolean;
  canReadClasses?: boolean;
  canReadFiscalPeriods?: boolean;
}

export function FixedAssetPage({
  fixedAssetId,
  onBack,
  canRevise = true,
  canDispose = true,
  canReadClasses = true,
  canReadFiscalPeriods = true,
}: FixedAssetPageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const assetQuery = useFixedAsset(fixedAssetId);
  const scheduleQuery = useDepreciationSchedule(fixedAssetId);
  const classesQuery = useAssetClasses(canReadClasses);
  const periodsQuery = useFiscalPeriods(canReadFiscalPeriods);
  const asset = assetQuery.data;

  const [newLife, setNewLife] = useState("");
  const [newSalvage, setNewSalvage] = useState("");
  const [revisionReason, setRevisionReason] = useState("");
  const [revision, setRevision] = useState<FixedAssetEstimateRevisionResult | null>(null);
  const [disposalDate, setDisposalDate] = useState(todayIsoDate);
  const [proceeds, setProceeds] = useState("");
  const [disposal, setDisposal] = useState<FixedAssetDisposal | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const periodName = useMemo(() => {
    const byId = new Map((periodsQuery.data ?? []).map((period) => [period.id, period.name]));
    return (id: string): string => byId.get(id) ?? id;
  }, [periodsQuery.data]);

  const reviseMutation = useIdempotentMutation<
    FixedAssetEstimateRevisionResult,
    { fixedAssetId: string; body: FixedAssetEstimateRevision }
  >({
    mutationFn: (input, key) => reviseFixedAssetEstimates(input.fixedAssetId, input.body, key),
    onSuccess: (result) => {
      setRevision(result);
      setNewLife("");
      setNewSalvage("");
      setRevisionReason("");
      void queryClient.invalidateQueries({ queryKey: financeKeys.fixedAssets.all });
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const disposeMutation = useIdempotentMutation<
    FixedAssetDisposal,
    { fixedAssetId: string; body: FixedAssetDisposalCreate }
  >({
    mutationFn: (input, key) => disposeFixedAsset(input.fixedAssetId, input.body, key),
    onSuccess: (result) => {
      setDisposal(result);
      void queryClient.invalidateQueries({ queryKey: financeKeys.fixedAssets.all });
    },
    onError: (error) => surfaceProblem(error, t),
  });

  if (assetQuery.isLoading) {
    return <div className="p-4 text-sm text-muted-foreground">{t("app.loading", "Loading…")}</div>;
  }

  if (asset === undefined) {
    return (
      <div className="flex flex-col gap-4 p-4">
        <ProblemBanner>
          {t("finance.fixedAssets.notFound", "This asset could not be loaded.")}
        </ProblemBanner>
        <Button type="button" variant="outline" onClick={onBack}>
          {t("finance.fixedAssets.back", "Back to the register")}
        </Button>
      </div>
    );
  }

  const assetClass = (classesQuery.data ?? []).find((candidate) => candidate.id === asset.class_id);
  const disposed = asset.status === "disposed";

  const submitRevision = () => {
    const life = newLife.trim() === "" ? null : Number.parseInt(newLife, 10);
    if (newLife.trim() !== "" && (life === null || Number.isNaN(life) || life < 1 || life > 1200)) {
      setFormError(
        t("finance.fixedAssets.lifeInvalid", "Useful life is between 1 and 1200 months."),
      );
      return;
    }
    const salvage =
      newSalvage.trim() === "" ? null : normalizeDecimal(newSalvage, { scale: AMOUNT_SCALE });
    if (newSalvage.trim() !== "" && salvage === null) {
      setFormError(
        t(
          "finance.fixedAssets.salvageInvalid",
          "Enter the salvage value with at most four decimals.",
        ),
      );
      return;
    }
    if (life === null && salvage === null) {
      setFormError(
        t(
          "finance.fixedAssets.revisionEmpty",
          "A revision has to change the useful life, the salvage value, or both.",
        ),
      );
      return;
    }
    setFormError(null);
    reviseMutation.mutate({
      fixedAssetId: asset.id,
      body: {
        ...(life === null ? {} : { useful_life_months: life }),
        ...(salvage === null ? {} : { salvage_value: salvage }),
        ...(revisionReason.trim() === "" ? {} : { reason: revisionReason.trim() }),
      },
    });
  };

  const submitDisposal = () => {
    const wireProceeds = normalizeDecimal(proceeds, { scale: AMOUNT_SCALE });
    if (wireProceeds === null) {
      setFormError(
        t(
          "finance.fixedAssets.proceedsInvalid",
          "Enter the proceeds — zero for a scrapping — with at most four decimals.",
        ),
      );
      return;
    }
    setFormError(null);
    disposeMutation.mutate({
      fixedAssetId: asset.id,
      body: { disposal_date: disposalDate, proceeds: wireProceeds },
    });
  };

  const renderAmount = (value: string): string => {
    try {
      return formatQuantity(value, i18n.language, AMOUNT_SCALE);
    } catch {
      return value;
    }
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <PageHeader
        title={asset.asset_no}
        description={asset.description}
        actions={
          <Button type="button" variant="ghost" onClick={onBack}>
            <ArrowLeft className="size-4 rtl:-scale-x-100" aria-hidden="true" />
            {t("finance.fixedAssets.back", "Back to the register")}
          </Button>
        }
      />

      <Card>
        <CardContent className="flex flex-col gap-4 pt-6">
          <StatusBadge
            status={asset.status}
            label={t(`finance.fixedAssets.statusOption.${asset.status}`, asset.status)}
            variants={ASSET_STATUS_VARIANTS}
          />
          <DefinitionList
            rows={[
              {
                label: t("finance.fixedAssets.class", "Asset class"),
                value:
                  assetClass === undefined
                    ? asset.class_id
                    : `${assetClass.code} · ${assetClass.name}`,
              },
              {
                label: t("finance.fixedAssets.capitalizationDate", "Capitalization date"),
                value: <span dir="ltr">{asset.capitalization_date}</span>,
              },
              {
                label: t("finance.fixedAssets.cost", "Acquisition cost"),
                value: <MoneyDisplay value={asset.acquisition_cost} />,
              },
              {
                label: t("finance.fixedAssets.salvage", "Salvage value"),
                value: <MoneyDisplay value={asset.salvage_value} />,
              },
              {
                label: t("finance.fixedAssets.accumulated", "Posted depreciation"),
                value: <MoneyDisplay value={asset.accumulated_depreciation} />,
              },
              {
                label: t("finance.fixedAssets.nbv", "Net book value"),
                value: <MoneyDisplay value={asset.net_book_value} />,
              },
              {
                label: t("finance.fixedAssets.method", "Method"),
                value: t(`finance.fixedAssets.methodOption.${asset.method}`, asset.method),
              },
              {
                label: t("finance.fixedAssets.usefulLife", "Useful life (months)"),
                value: <span dir="ltr">{asset.useful_life_months}</span>,
              },
              {
                label: t("finance.fixedAssets.decliningFactor", "Declining factor"),
                value: <span dir="ltr">{asset.declining_factor}</span>,
              },
            ]}
          />
          {disposed ? (
            <NoticeBanner>
              {t(
                "finance.fixedAssets.disposedNotice",
                "This asset is disposed. That is terminal and it no longer depreciates.",
              )}
            </NoticeBanner>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("finance.fixedAssets.scheduleSection", "Depreciation schedule")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {scheduleQuery.data === undefined ? (
            <p className="text-sm text-muted-foreground">{t("app.loading", "Loading…")}</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th scope="col" className="p-2 text-start">
                    #
                  </th>
                  <th scope="col" className="p-2 text-start">
                    {t("finance.fixedAssets.period", "Period")}
                  </th>
                  <th scope="col" className="p-2 text-end">
                    {t("finance.fixedAssets.charge", "Charge")}
                  </th>
                  <th scope="col" className="p-2 text-end">
                    {t("finance.fixedAssets.accumulatedAfter", "Accumulated after")}
                  </th>
                  <th scope="col" className="p-2 text-start">
                    {t("finance.fixedAssets.lineStatus", "Status")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {scheduleQuery.data.items.map((line) => (
                  <tr key={line.id} className="border-b">
                    <td className="p-2 tabular-nums">{line.sequence_no}</td>
                    <td className="p-2">{periodName(line.fiscal_period_id)}</td>
                    <td dir="ltr" className="p-2 text-end tabular-nums">
                      {renderAmount(line.amount)}
                    </td>
                    <td dir="ltr" className="p-2 text-end tabular-nums">
                      {renderAmount(line.accumulated_after)}
                    </td>
                    <td className="p-2">
                      {t(`finance.fixedAssets.scheduleStatus.${line.status}`, line.status)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {!disposed && canRevise ? (
        <Card>
          <CardHeader>
            <CardTitle>
              <h2>{t("finance.fixedAssets.revisionSection", "Revise the estimates")}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <p className="text-sm text-muted-foreground">
              {t(
                "finance.fixedAssets.revisionHint",
                "Prospective only. Periods that already posted stay exactly as they are; the undepreciated balance is spread over what remains.",
              )}
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <TextField
                id="revision-life"
                label={t("finance.fixedAssets.newLife", "Revised total life (months)")}
                value={newLife}
                onChange={setNewLife}
                hint={t(
                  "finance.fixedAssets.newLifeHint",
                  "The TOTAL life, including periods already posted.",
                )}
                ltr
              />
              <DecimalField
                id="revision-salvage"
                label={t("finance.fixedAssets.newSalvage", "Revised salvage value")}
                value={newSalvage}
                onChange={setNewSalvage}
                decimal={{ scale: AMOUNT_SCALE }}
                invalidMessage={t(
                  "finance.fixedAssets.salvageInvalid",
                  "Enter the salvage value with at most four decimals.",
                )}
              />
              <TextField
                id="revision-reason"
                label={t("finance.fixedAssets.revisionReason", "Reason")}
                value={revisionReason}
                onChange={setRevisionReason}
                maxLength={512}
              />
            </div>
            <div>
              <Button type="button" loading={reviseMutation.isPending} onClick={submitRevision}>
                {t("finance.fixedAssets.revise", "Revise estimates")}
              </Button>
            </div>
            {revision === null ? null : (
              <NoticeBanner>
                {t(
                  "finance.fixedAssets.revisionResult",
                  "{{posted}} posted periods left untouched, {{replaced}} scheduled periods regenerated.",
                  { posted: revision.posted_periods, replaced: revision.replaced_periods },
                )}
              </NoticeBanner>
            )}
          </CardContent>
        </Card>
      ) : null}

      {!disposed && canDispose ? (
        <Card>
          <CardHeader>
            <CardTitle>
              <h2>{t("finance.fixedAssets.disposalSection", "Dispose of the asset")}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <p className="text-sm text-muted-foreground">
              {t(
                "finance.fixedAssets.disposalHint",
                "Gain or loss is proceeds minus net book value, counting only depreciation that posted. Disposal is terminal.",
              )}
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <DateField
                id="disposal-date"
                label={t("finance.fixedAssets.disposalDate", "Disposal date")}
                value={disposalDate}
                onChange={setDisposalDate}
              />
              <DecimalField
                id="disposal-proceeds"
                label={t("finance.fixedAssets.proceeds", "Proceeds")}
                value={proceeds}
                onChange={setProceeds}
                decimal={{ scale: AMOUNT_SCALE }}
                hint={t("finance.fixedAssets.proceedsHint", "Zero for a scrapping.")}
                invalidMessage={t(
                  "finance.fixedAssets.proceedsInvalid",
                  "Enter the proceeds — zero for a scrapping — with at most four decimals.",
                )}
              />
            </div>
            <div>
              <Button
                type="button"
                variant="destructive"
                loading={disposeMutation.isPending}
                onClick={submitDisposal}
              >
                {t("finance.fixedAssets.dispose", "Dispose and derecognize")}
              </Button>
            </div>
            {disposal === null ? null : (
              <div className="rounded-md border p-3 text-sm" data-testid="disposal-result">
                <p>
                  {t("finance.fixedAssets.disposalGainLoss", "Gain or loss")}{" "}
                  <MoneyDisplay value={disposal.gain_loss} />
                </p>
                <p className="text-muted-foreground">
                  {t("finance.fixedAssets.disposalDerecognized", "Derecognized at gross:")}{" "}
                  <MoneyDisplay value={disposal.acquisition_cost} /> ·{" "}
                  <MoneyDisplay value={disposal.accumulated_depreciation} />
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}

      {formError === null ? null : <ProblemBanner>{formError}</ProblemBanner>}
    </div>
  );
}
